import React, { ReactNode } from "react";
import "./Card.css";

interface CardProps {
  children: ReactNode;
  className?: string;
  variant?: "default" | "elevated" | "outlined" | "minimal";
  padding?: "none" | "sm" | "md" | "lg";
  hover?: boolean;
  clickable?: boolean;
  onClick?: () => void;
}

export const Card: React.FC<CardProps> = ({
  children,
  className = "",
  variant = "default",
  padding = "md",
  hover = false,
  clickable = false,
  onClick,
}) => {
  const baseClasses = "card";
  const variantClasses = `card--${variant}`;
  const paddingClasses = `card--padding-${padding}`;
  const hoverClasses = hover ? "card--hover" : "";
  const clickableClasses = clickable ? "card--clickable" : "";

  const combinedClasses = [
    baseClasses,
    variantClasses,
    paddingClasses,
    hoverClasses,
    clickableClasses,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const cardProps = {
    className: combinedClasses,
    ...(clickable &&
      onClick && {
        onClick,
        role: "button",
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        },
      }),
  };

  return <div {...cardProps}>{children}</div>;
};

interface CardHeaderProps {
  children: ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  className = "",
}) => <div className={`card__header ${className}`}>{children}</div>;

interface CardBodyProps {
  children: ReactNode;
  className?: string;
  padding?: "none" | "sm" | "md" | "lg";
}

export const CardBody: React.FC<CardBodyProps> = ({
  children,
  className = "",
  padding = "md",
}) => {
  const paddingClass = padding !== "md" ? `card__body--padding-${padding}` : "";
  const combinedClasses = [`card__body`, paddingClass, className]
    .filter(Boolean)
    .join(" ");

  return <div className={combinedClasses}>{children}</div>;
};

interface CardFooterProps {
  children: ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({
  children,
  className = "",
}) => <div className={`card__footer ${className}`}>{children}</div>;

interface CardImageProps {
  src: string;
  alt: string;
  className?: string;
}

export const CardImage: React.FC<CardImageProps> = ({
  src,
  alt,
  className = "",
}) => (
  <div className={`card__image ${className}`}>
    <img src={src} alt={alt} />
  </div>
);

export default Card;
